/**
 * Bundle 44a - Data Health hub.
 *
 * Replaces the "Maintenance" junk drawer as the primary admin surface
 * for data quality issues. Every card shows plain-English framing
 * (impact + cause) and a single primary action verb. Zero JSON on
 * screen; underlying maintenance ops still exist at /admin/advanced-tools.
 *
 * Persona: event manager, not engineer. "15 attendees can't edit
 * their names" is what they see, not "15 rows with weak first_name".
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, CheckCircle2, ChevronRight,
  BellOff, Loader2, RefreshCw, ShieldAlert,
} from 'lucide-react';
import api from '@/utils/api';
// Bundle 94 - production privacy-header verifier + Stripe mode indicator.
import { PrivacyStripeHealthTiles } from './components/PrivacyStripeHealthTiles';
// Bundle 95.1 - Campaign schema drift monitor tile.
import SchemaDriftTile from './components/SchemaDriftTile';
import BuildInfoCard from './components/BuildInfoCard';
import CustomConversionModal from './components/CustomConversionModal';
// Bundle 139 - Framing sentinel status tile.
import FramingSentinelTile from './components/FramingSentinelTile';

const SEV_BADGE = {
  red: {
    icon: AlertCircle,
    dot: 'bg-red-500',
    ring: 'ring-red-100',
    text: 'text-red-700',
    label: 'Broken user experience',
  },
  amber: {
    icon: AlertTriangle,
    dot: 'bg-amber-500',
    ring: 'ring-amber-100',
    text: 'text-amber-700',
    label: 'Data quality debt',
  },
  green: {
    icon: CheckCircle2,
    dot: 'bg-emerald-500',
    ring: 'ring-emerald-100',
    text: 'text-emerald-700',
    label: 'Healthy',
  },
};

const HealthCard = ({ card, onPrimary, onSecondary, onSnooze, busy }) => {
  const sev = SEV_BADGE[card.severity] || SEV_BADGE.amber;
  const SevIcon = sev.icon;
  const isBusy = busy === card.id;
  return (
    <Card
      data-testid={`health-card-${card.id}`}
      className={`transition-shadow hover:shadow-md ${card.snoozed ? 'opacity-60' : ''}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 rounded-full p-2 ring-4 ${sev.ring}`}>
            <SevIcon className={`h-5 w-5 ${sev.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-semibold text-neutral-900">
                  {card.title}
                </h3>
                <div className={`text-xs font-medium ${sev.text}`}>
                  {sev.label}
                  {card.snoozed ? ' * snoozed' : ''}
                </div>
              </div>
              <div
                data-testid={`health-card-count-${card.id}`}
                className="text-3xl font-display font-bold text-neutral-900 tabular-nums"
              >
                {card.count}
                {card.count_suffix ? (
                  <span className="text-lg font-medium text-neutral-500">
                    {card.count_suffix}
                  </span>
                ) : null}
              </div>
            </div>
            <p className="mt-2 text-sm text-neutral-800">{card.impact}</p>
            {card.detail ? (
              <p className="mt-1 text-xs text-neutral-500">{card.detail}</p>
            ) : null}
            {card.cause ? (
              <p className="mt-1 text-xs text-neutral-400 italic">
                {card.cause}
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {card.primary_action ? (
                <Button
                  size="sm"
                  onClick={() => onPrimary(card)}
                  disabled={isBusy}
                  data-testid={`health-card-primary-${card.id}`}
                  className="bg-primary-900 hover:bg-primary-800 text-white"
                >
                  {isBusy ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Working...</>
                  ) : (
                    card.primary_action.label
                  )}
                </Button>
              ) : null}
              {card.secondary_action ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSecondary(card)}
                  data-testid={`health-card-secondary-${card.id}`}
                >
                  {card.secondary_action.label}
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onSnooze(card)}
                data-testid={`health-card-snooze-${card.id}`}
                className="text-neutral-500 hover:text-neutral-800"
              >
                <BellOff className="h-3.5 w-3.5 mr-1" />
                {card.snoozed ? 'Unsnooze' : 'Snooze 7 days'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDataHealthPage = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [customConvOpen, setCustomConvOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [counts, setCounts] = useState({ red: 0, amber: 0, green: 0 });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/health/cards');
      setCards(data.cards || []);
      setCounts(data.meta?.counts || { red: 0, amber: 0, green: 0 });
    } catch (e) {
      toast.error('Could not load data health cards.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPrimary = async (card) => {
    if (!card.primary_action) return;
    const action = card.primary_action;
    // Bundle 68 Task B - Meta Custom Conversion modal on funnel cards.
    if (action.op === 'create-custom-conversion') {
      setCustomConvOpen(true);
      return;
    }
    if (action.route) { navigate(action.route); return; }
    if (!action.endpoint) return;
    setBusy(card.id);
    try {
      const method = (action.method || 'POST').toLowerCase();
      const resp = method === 'get'
        ? await api.get(action.endpoint)
        : await api.post(action.endpoint);
      const summary = resp?.data?.summary || resp?.data;
      const repaired = summary?.repaired ?? summary?.recovered_from_rsvp ?? summary?.would_repair;
      toast.success(
        repaired != null
          ? `Done. ${repaired} fixed.`
          : 'Done.',
      );
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Action failed.';
      toast.error(typeof msg === 'string' ? msg : 'Action failed.');
    } finally {
      setBusy(null);
    }
  };

  const onSecondary = (card) => {
    if (!card.secondary_action) return;
    if (card.secondary_action.route) navigate(card.secondary_action.route);
  };

  const onSnooze = async (card) => {
    try {
      const path = card.snoozed
        ? `/admin/health/unsnooze/${card.id}`
        : `/admin/health/snooze/${card.id}`;
      await api.post(path);
      toast.success(card.snoozed ? 'Un-snoozed.' : 'Snoozed for 7 days.');
      load();
    } catch (e) {
      toast.error('Could not update snooze.');
    }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="admin-data-health-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-neutral-900">
            Data Health
          </h1>
          <p className="mt-2 text-sm text-neutral-600 max-w-2xl">
            Problems worth acting on right now. Each card is a fixable
            issue affecting real attendees. Click a card&apos;s action button
            to run the fix without opening the maintenance tools.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-data-health-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="mb-6 text-sm text-neutral-600" data-testid="admin-data-health-analytics-link">
        Prefer a triage view with recommended fixes plus the funnel and CAPI
        backfill tools? Open{' '}
        <button
          type="button"
          className="text-primary-800 underline hover:text-primary-900"
          onClick={() => navigate('/admin/analytics?tab=health')}
          data-testid="admin-data-health-open-analytics"
        >
          Admin Analytics
        </button>
        .
      </div>

      {/* Bundle 94 - production privacy-header verifier + Stripe mode. */}
      <PrivacyStripeHealthTiles />

      {/* Bundle 95.1 - Campaign schema drift monitor. */}
      <div className="mb-6">
        <SchemaDriftTile />
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
        <div className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-800 px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span data-testid="health-counts-red">{counts.red}</span> broken
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-800 px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span data-testid="health-counts-amber">{counts.amber}</span> attention
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-800 px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span data-testid="health-counts-green">{counts.green}</span> healthy
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-neutral-500">Loading...</div>
      ) : cards.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-display text-lg font-semibold">
              Nothing to fix right now
            </h3>
            <p className="text-sm text-neutral-500 mt-1">
              We&apos;ll surface issues here as soon as they appear.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {cards.map((c) => (
            <HealthCard
              key={c.id}
              card={c}
              onPrimary={onPrimary}
              onSecondary={onSecondary}
              onSnooze={onSnooze}
              busy={busy}
            />
          ))}
        </div>
      )}

      <Card className="mt-8 border-neutral-200 bg-neutral-50">
        <CardContent className="p-4 flex items-start gap-3">
          <ShieldAlert className="h-4 w-4 text-neutral-500 mt-0.5" />
          <div className="text-xs text-neutral-600">
            Looking for the old maintenance page? It moved to
            <button
              onClick={() => navigate('/admin/advanced-tools')}
              className="ml-1 underline hover:text-neutral-900"
              data-testid="admin-data-health-advanced-tools-link"
            >
              Advanced tools
            </button>
            . Most fixes are on Data Health now.
          </div>
        </CardContent>
      </Card>

      <div className="mt-4">
        <BuildInfoCard />
      </div>

      {/* Bundle 139 P0-3 - Framing sentinel status tile. */}
      <div className="mt-4">
        <FramingSentinelTile />
      </div>

      <CustomConversionModal
        open={customConvOpen}
        onOpenChange={setCustomConvOpen}
        defaultPreset="warm_cart_abandoners"
      />
    </div>
  );
};

export default AdminDataHealthPage;
